import {useMutation} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {
  FetchRequestError,
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  StaleContentError,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

/** Thrown when Copilot summary generation fails; `message` is safe to show the user directly. */
export class PullRequestSummaryError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PullRequestSummaryError'
  }
}

/**
 * `StaleContentError`'s `freshData` when the job itself failed, but a previous completion was
 * recovered and returned instead.
 */
export interface PullRequestSummaryStaleData {
  /** The job ID to pass as `previousJobId` on the next generation attempt. */
  jobId: string | null
}

/** Result of a completed summary generation job. */
export interface PullRequestSummaryCompletion {
  completion: string | null
  jobId: string | null
  feedbackUrl: string | null
  feedbackAuthToken: string | null
}

interface GenerateSummaryVariables {
  baseRevision: string
  headRevision: string
  headRepositoryId: number
  customPrompt?: string
  previousJobId?: string | null
}

const INITIAL_POLL_DELAY_MS = 2000
const POLL_BACKOFF_MULTIPLIER = 1.2
// 4 minutes because the summary generation can take a long time for large PRs
const POLL_TIMEOUT_MS = 4 * 60 * 1000

type JobPayload = {
  job: {
    id?: string
    url?: string
    feedback_url?: string
    feedback_auth_token?: string
    error_message?: string
    context?: {completion?: string | null}
  }
}

async function pollJobUntilSettled(jobUrl: string): Promise<{status: number; json: JobPayload}> {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  let delay = INITIAL_POLL_DELAY_MS
  for (;;) {
    const response = await fetchWithErrorHandling(jobUrl)
    if (response.status === 202) {
      if (Date.now() >= deadline) {
        throw new PullRequestSummaryError('Copilot took too long to generate a summary. Please try again.')
      }
      await new Promise(resolve => setTimeout(resolve, delay))
      delay *= POLL_BACKOFF_MULTIPLIER
      continue
    }
    const json = await parseJSONWithBetterErrors(response)
    return {status: response.status, json}
  }
}

async function postCompletionsRequest(completionsUrl: string, body: URLSearchParams): Promise<Response> {
  try {
    return await reactFetch(completionsUrl, {
      method: 'POST',
      body,
      headers: {Accept: 'application/json'},
    })
  } catch (e) {
    throw new FetchRequestError(e)
  }
}

export function useGeneratePullRequestSummaryMutation(completionsUrl: string) {
  return useMutation({
    mutationFn: async ({
      baseRevision,
      headRevision,
      headRepositoryId,
      customPrompt,
      previousJobId,
    }: GenerateSummaryVariables): Promise<PullRequestSummaryCompletion> => {
      const body = new URLSearchParams({
        base_revision: baseRevision,
        head_revision: headRevision,
        head_repo_id: String(headRepositoryId),
      })
      if (customPrompt) body.set('custom_prompt', customPrompt)
      if (previousJobId) body.set('previous_job_id', previousJobId)

      const response = await postCompletionsRequest(completionsUrl, body)
      const json = (await parseJSONWithBetterErrors(response)) as JobPayload
      if (!response.ok || !json?.job?.url) {
        throw new PullRequestSummaryError(json?.job?.error_message || 'Copilot encountered a temporary error.')
      }

      const {status, json: jobJson} = await pollJobUntilSettled(json.job.url)
      const completion = jobJson?.job?.context?.completion ?? null

      if (status >= 200 && status < 300) {
        return {
          completion,
          jobId: jobJson.job.id ?? null,
          feedbackUrl: jobJson.job.feedback_url ?? null,
          feedbackAuthToken: jobJson.job.feedback_auth_token ?? null,
        }
      }

      // A 500 with a recovered completion means the job itself failed, but a previous
      // successful run's result was returned instead: show it with a staleness warning.
      if (status === 500 && completion) {
        throw new StaleContentError<PullRequestSummaryStaleData>({jobId: jobJson.job.id ?? null})
      }

      throw new PullRequestSummaryError(jobJson?.job?.error_message || 'Copilot encountered a temporary error.')
    },
  })
}
